import type { StateCreator } from "zustand";
import { actionNameBuilder } from "../../utilities/storeActionNameBuilder";
import type { ApiEndpoint } from "../../types";

export interface PrimarySelectionState {
  selectedEntityId: string | number | null;
  selectedEndpoint: ApiEndpoint | null;
  showRelationShip: boolean;
}

export interface PrimarySelectionActions {
  setSelectedEntityId: (id: string | number | null) => void;
  setSelectedEndpoint: (endpoint: ApiEndpoint | null) => void;
  setShowRelationship: (value: boolean) => void;
  setSelectedPrimaryStates: (values: Partial<PrimarySelectionState>) => void;
  clearPrimarySelection: () => void;
}

export interface PrimarySelectionSlice {
  primary: PrimarySelectionState & {
    actions: PrimarySelectionActions;
  };
}

const actionName = actionNameBuilder("primarySelection");

/**
 * Slice creator typed generically so it composes without TS errors in the combined store.
 * The `T` parameter is inferred by callers (e.g. index.ts) as the full combined store type.
 */
export const createPrimarySelectionSlice = <T extends PrimarySelectionSlice>(
  ...[set]: Parameters<StateCreator<T, [["zustand/devtools", never], ["zustand/immer", never]], []>>
): PrimarySelectionSlice => ({
  primary: {
    // Initial state
    selectedEntityId: null,
    selectedEndpoint: null,
    showRelationShip: false,

    // Actions
    actions: {
      setSelectedEntityId: (id) =>
        set(
          (state) => {
            state.primary.selectedEntityId = id;
          },
          undefined,
          actionName("setSelectedEntityId")
        ),
      setShowRelationship: (value) =>
        set(
          (state) => {
            state.primary.showRelationShip = value;
          },
          undefined,
          actionName("setShowRelationship")
        ),

      setSelectedEndpoint: (endpoint) =>
        set(
          (state) => {
            state.primary.selectedEndpoint = endpoint;
          },
          undefined,
          actionName("setSelectedEndpoint")
        ),
      setSelectedPrimaryStates: (values: Partial<PrimarySelectionState>) =>
        set(
          (state) => {
            state.primary = { ...state.primary, ...values };
          },
          undefined,
          actionName("setSelectedPrimaryStates")
        ),

      clearPrimarySelection: () =>
        set(
          (state) => {
            state.primary.selectedEntityId = null;
            state.primary.selectedEndpoint = null;
            state.primary.showRelationShip = false;
          },
          undefined,
          actionName("clearSelection")
        )
    }
  }
});
